import React from "react"
import NextButton from "./NextButton"
import BackButton from "./BackButton"

type optionsType = {
    label: string
    value: string
    type: string
    identifier: string
}

type InputFieldType = {
    onNext: () => void
    onBack?: () => void
    option: optionsType[]
    onChange: (key: string, value: string) => void
    values: Record<string, string>
    submit: boolean
    onSubmit: () => void
}

export default function InputField(props: InputFieldType) {
    const allFieldsFilled = props.option.every((input) => {
        // Check if field is optional (label contains "optional")
        const isOptional = input.label.toLowerCase().includes("optional")
        if (isOptional) return true
        return (props.values[input.identifier] ?? "").trim() !== ""
    })

    const [errors, setErrors] = React.useState<Record<string, string>>({})

    const validateField = (label: string, value: string, type: string): string => {
        // If value is empty, it's only an error if it's not optional
        if (!value.trim()) {
            return "Pflichtfeld"
        }

        if (type === "email") {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
            if (!emailRegex.test(value)) {
                return "Falsches E-Mail Format"
            }
        }

        if (type === "tel") {
            const phoneRegex = /^\+?\d{1,4}[\s-]?\(?\d+\)?[\s-]?\d+[\s-]?\d+$/
            if (!phoneRegex.test(value)) {
                return "Ungültige Telefonnummer"
            }
        }

        return ""
    }

    const hasErrors = Object.values(errors).some((error) => error !== "")

    const canProceed = allFieldsFilled && !hasErrors

    return (
        <div className="flex flex-col items-end">
            <div className="flex flex-col sm:flex-row sm:flex-wrap py-8 gap-8 w-full">
                {props.option.map((input, index) => {
                    const isFilled = (props.values[input.identifier] ?? "").trim() !== ""

                    if (input.type === "textarea") {
                        return (
                            <div key={index} className="relative w-full">
                                <textarea
                                    placeholder={input.label}
                                    value={props.values[input.identifier] || ""}
                                    onChange={(e) => props.onChange(input.identifier, e.target.value)}
                                    className={`peer w-full bg-base-100 text-2xl focus:outline-none border-b ${
                                        isFilled ? "border-b-secondary" : "border-b-primary"
                                    }  -mb-[6px]`}
                                    rows={4}
                                />
                                <div className="absolute bottom-0 left-0 h-[1px] w-full bg-secondary origin-left scale-x-0 peer-focus:scale-x-100 transition-transform duration-[2000ms] " />
                            </div>
                        )
                    }

                    return (
                        <div key={index} className="w-full sm:w-[calc(50%-1rem)]">
                            <div className="relative ">
                                <input
                                    type={input.type}
                                    placeholder={input.label.toLowerCase().includes("optional") ? input.label : input.label + " *"}
                                    value={props.values[input.identifier] || ""}
                                    onChange={(e) => props.onChange(input.identifier, e.target.value)}
                                    onBlur={(e) => {
                                        // Only validate if field is not optional
                                        const isOptional = input.label.toLowerCase().includes("optional")
                                        if (!isOptional || e.target.value.trim() !== "") {
                                            const error = validateField(input.identifier, e.target.value, input.type)
                                            setErrors((prev) => ({ ...prev, [input.identifier]: error }))
                                        } else if (isOptional && e.target.value.trim() === "") {
                                            // Clear errors for optional empty fields
                                            setErrors((prev) => ({ ...prev, [input.identifier]: "" }))
                                        }
                                    }}
                                    className={`peer w-full bg-base-100 text-2xl focus:outline-none border-b ${
                                        isFilled ? "border-b-secondary" : "border-b-primary"
                                    } pb-2`}
                                />
                                <div className="absolute bottom-0 left-0 h-[1px] w-full bg-secondary origin-left scale-x-0 peer-focus:scale-x-100 transition-transform duration-500" />
                            </div>
                            {errors[input.identifier] && <p className="text-red-500 text-sm mt-1">{errors[input.identifier]}</p>}
                        </div>
                    )
                })}
            </div>
            <div className="flex gap-4 w-full sm:w-fit">
                {props.onBack && <BackButton onBack={props.onBack} />}
                <NextButton filled={canProceed} submit={props.submit} onNext={props.onNext} onSubmit={props.onSubmit} />
            </div>
        </div>
    )
}
